// Figma: https://www.figma.com/design/nykbXADUTliE6d7xv6FjcQ/NEW-TSAB-Shared-Customer-Library?node-id=3228-31672
// Component: SelectedPayment — payment option row with icon, title/info, price, radio indicator

import { useRef } from 'react'
import { Field } from 'formik'
import classnames from 'classnames'
import Pill from '~/components/ui/Pill'

// Matches Figma Radiobutton (node 52:1061 / 52:1063) — same as CarRadioField
function RadioIcon({ checked }) {
  if (checked) {
    return (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true" className="shrink-0">
        <rect x="0.25" y="0.25" width="21.5" height="21.5" rx="10.75" fill="#FFCC00" />
        <rect x="0.25" y="0.25" width="21.5" height="21.5" rx="10.75" stroke="#BA8200" strokeWidth="0.5" />
        <path fillRule="evenodd" clipRule="evenodd" d="M16.1404 6.74141C16.3176 6.88535 16.4061 6.95732 16.4635 7.03588C16.6206 7.25109 16.6517 7.53347 16.5452 7.77772C16.5064 7.86689 16.4356 7.95642 16.294 8.13548L10.6357 15.2931C10.4676 15.5057 10.3836 15.6121 10.2776 15.6621C10.1843 15.7062 10.0797 15.7203 9.97813 15.7026C9.86267 15.6825 9.75339 15.6023 9.53484 15.4419L5.92882 12.7955C5.73218 12.6512 5.63385 12.579 5.57038 12.4981C5.40396 12.2859 5.36426 12.0007 5.46639 11.7512C5.50535 11.656 5.58023 11.5597 5.72998 11.3672C5.87077 11.1862 5.94116 11.0957 6.01956 11.0362C6.2248 10.8806 6.49554 10.8411 6.73664 10.9317C6.82875 10.9664 6.92204 11.033 7.10862 11.1664L9.8395 13.1178L14.7258 6.89943C14.8706 6.71509 14.943 6.62293 15.0224 6.56357C15.2401 6.40085 15.5287 6.36861 15.7769 6.47929C15.8675 6.51967 15.9585 6.59358 16.1404 6.74141Z" fill="#17181A" />
      </svg>
    )
  }
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true" className="shrink-0">
      <circle cx="11" cy="11" r="10.5" stroke="#8c8c8c" strokeWidth="1" />
    </svg>
  )
}

function InfoCircleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="6" stroke="#8c8c8c" strokeWidth="1.2" />
      <rect x="6.4" y="6" width="1.2" height="4.5" rx="0.6" fill="#8c8c8c" />
      <circle cx="7" cy="4.25" r="0.7" fill="#8c8c8c" />
    </svg>
  )
}

export default function RadioField({
  field,
  form: { setFieldValue },
  title,
  info,
  icon,
  price,
  originalPrice,
  voucherPill,
  onInfo,
  disabled,
  hidden,
  innerRef,
  tabIndex,
  // legacy props — accepted but not used in new layout
  alternateBackground,
  disabledDescription,
  ...props
}) {
  const isChecked = field.checked
  const clickRef = useRef()

  const handleClick = () => {
    if (disabled) return
    ;(innerRef || clickRef)?.current?.click()
  }

  const handleKeyDown = e => {
    if (e.keyCode === 32) e.preventDefault()
  }

  const handleKeyUp = e => {
    if (e.keyCode === 32) {
      e.preventDefault()
      setFieldValue(field.name, field.value)
      if (props.onClick) props.onClick()
    }
  }

  const theme = typeof CONFIG !== 'undefined' && CONFIG.svea_module ? 'svea-taxi' : 'taxi-stockholm'

  if (hidden) return null

  return (
    <div
      onClick={handleClick}
      className={classnames(
        'flex gap-3 items-center py-3 w-full cursor-pointer relative',
        disabled && 'pointer-events-none',
      )}
    >
      {/* Info button — top right corner */}
      {onInfo && (
        <button
          type="button"
          onClick={e => { e.stopPropagation(); onInfo() }}
          className="absolute top-1 right-0 p-0.5"
          tabIndex={-1}
          aria-label="More information"
        >
          <InfoCircleIcon />
        </button>
      )}

      {/* Payment icon */}
      {icon && (
        <div className={classnames('shrink-0 size-11 flex items-center justify-center overflow-clip', disabled && 'opacity-[0.38]')}>
          <img
            src={`/app/themes/${theme}/static/img/option-icons/${icon}.png`}
            alt=""
            className="size-full object-contain"
          />
        </div>
      )}

      {/* Text + pill column — title/info dimmed when disabled, disabledDescription pill stays full opacity */}
      <div className="flex-1 flex flex-col items-start min-w-0">
        <span className={classnames('text-[17px] font-medium tracking-[-0.34px] leading-[22px] text-on-surface', disabled && 'opacity-[0.38]')}>{title}</span>
        {info && <span className={classnames('text-[15px] leading-5 text-on-surface-variant', disabled && 'opacity-[0.38]')}>{info}</span>}
        {disabledDescription && <div className="mt-1"><Pill text={disabledDescription} /></div>}
        {voucherPill && <div className="mt-1"><Pill text={voucherPill} type="user-choice" /></div>}
      </div>

      {/* Price column */}
      {price && (
        <div className={classnames('flex flex-col items-end gap-0.5 shrink-0', disabled && 'opacity-[0.38]')}>
          <span className="text-[17px] font-bold uppercase tracking-[0.68px] leading-5 text-on-surface whitespace-nowrap">
            {price}
          </span>
          {originalPrice && (
            <span className="text-[15px] text-on-surface-variant line-through leading-5 whitespace-nowrap">
              {originalPrice}
            </span>
          )}
        </div>
      )}

      {/* Radio indicator */}
      <label
        ref={innerRef || clickRef}
        tabIndex={disabled ? -1 : (tabIndex ?? 0)}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        className={classnames('shrink-0 cursor-pointer', disabled && 'opacity-[0.38]')}
        onClick={e => e.stopPropagation()}
        aria-label={title}
      >
        <RadioIcon checked={isChecked} />
        <Field
          type="radio"
          as="input"
          className="sr-only"
          name={field.name}
          onChange={field.onChange}
          onBlur={field.onBlur}
          disabled={disabled}
          {...props}
        />
      </label>
    </div>
  )
}
